import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Button
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

function ActivityLogView({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Get unique users, actors, and event types from the events for filter dropdowns
  const [allUsers, setAllUsers] = useState([]);
  const [allActors, setAllActors] = useState([]);
  const [allEventTypes, setAllEventTypes] = useState([]);

  const loadActivityLog = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,  // Backend uses 1-indexed pages
        limit: rowsPerPage
      };

      if (eventTypeFilter) params.eventType = eventTypeFilter;
      if (userFilter) params.userId = userFilter;
      if (actorFilter) params.actorId = actorFilter;
      if (startDateFilter) params.startDate = startDateFilter;
      if (endDateFilter) params.endDate = endDateFilter;

      const data = await clinicalSupervisorApi.getSupervisorActivityLog(token, params);
      setActivityData(data);

      // Use filter options from backend if available (only on initial/unfiltered load)
      if (data.filterOptions) {
        setAllEventTypes(data.filterOptions.eventTypes || []);
        setAllUsers(data.filterOptions.users || []);
        setAllActors(data.filterOptions.actors || []);
      }
    } catch (err) {
      console.error('Error loading activity log:', err);
      setError(err.response?.data?.msg || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivityLog();
  }, [token, page, rowsPerPage, eventTypeFilter, userFilter, actorFilter, startDateFilter, endDateFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ');
  };

  const getEventColor = (eventType) => {
    if (eventType.includes('LOGIN') || eventType.includes('LOGOUT')) return 'primary';
    if (eventType.includes('ERROR') || eventType.includes('FAILED')) return 'error';
    if (eventType.includes('SUPERVISOR')) return 'warning';
    if (eventType.includes('CREATED') || eventType.includes('ADDED')) return 'success';
    return 'default';
  };

  if (loading && !activityData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const { events = [], summary, totalEvents = 0, totalPages = 0, currentPage = 1 } = activityData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Activity Log
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive activity log for all supervised therapists and clients
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadActivityLog} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="h5" color="primary">
                  {summary.supervisedTherapists}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supervised Therapists
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="h5" color="success.main">
                  {summary.supervisedClients}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Clients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="h5" color="info.main">
                  {totalEvents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Events
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Event Type"
              select
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
            >
              <MenuItem value="">All Events</MenuItem>
              {allEventTypes.map((eventType) => (
                <MenuItem key={eventType} value={eventType}>
                  {formatEventType(eventType)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="User"
              select
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
            >
              <MenuItem value="">All Users</MenuItem>
              {allUsers.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name} ({user.role})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Actor"
              select
              value={actorFilter}
              onChange={(e) => {
                setActorFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
            >
              <MenuItem value="">All Actors</MenuItem>
              {allActors.map((actor) => (
                <MenuItem key={actor._id} value={actor._id}>
                  {actor.name} ({actor.role})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setEventTypeFilter('');
                  setUserFilter('');
                  setActorFilter('');
                  setStartDateFilter('');
                  setEndDateFilter('');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDateFilter}
              onChange={(e) => {
                setStartDateFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDateFilter}
              onChange={(e) => {
                setEndDateFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Showing {events.length} of {totalEvents} events
              {(eventTypeFilter || userFilter || actorFilter || startDateFilter || endDateFilter) && ' (filtered)'}
            </Typography>
            {!startDateFilter && !endDateFilter && (
              <Typography variant="caption" color="text.secondary" display="block">
                Default: Last 90 days only
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Activity Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No activity events found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event, index) => (
                <TableRow key={event._id || index} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(event.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatEventType(event.eventType)}
                      size="small"
                      color={getEventColor(event.eventType)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {event.userId?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.userId?.role || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {event.actorId?.name || 'System'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.actorId?.role || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {event.ipAddress && `IP: ${event.ipAddress}`}
                      {event.details && Object.keys(event.details).length > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                          {JSON.stringify(event.details, null, 2)}
                        </Box>
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalEvents}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default ActivityLogView;
